import { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { faAddressCard } from '@fortawesome/pro-light-svg-icons';
import { faChevronDown, faChevronRight, faPencil } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Button, Card, Divider, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';

import HeroCta from 'src/components/HeroCta';
import MainNav from 'src/components/MainNav';
import {
    StyledAccordion,
    StyledAccordionActions,
    StyledAccordionDetails,
    StyledAccordionSummary,
} from 'src/components/StyledAccordion';
import TeamMemberAvatar from 'src/components/TeamMemberAvatar';
import TopLevelPageHeader from 'src/components/TopLevelPageHeader';
import { AdlNames, CareIcons, DayToDayNames } from 'src/constants/CareInstructions';
import { useAnalytics } from 'src/contexts/AnalyticsContext';
import useResponsive from 'src/hooks/useResponsive';
import { useTeamMemberData } from 'src/hooks/useTeamMemberData';
import { InnerMaxWidthContainer } from 'src/layouts/InnerMaxWidthContainer';
import { selectAdls, selectDayToDays } from 'src/store/slices/careInstructionsSlice';
import { PronounLabel, getPronoun, getTeamMemberDisplayName } from 'src/utils/nameUtils';

const PageName = 'Care Page';

export const Route = createFileRoute('/(app)/care/')({
    component: CareComponent,
});

function CareComponent() {
    const { isMobile } = useResponsive();
    const navigate = useNavigate();
    const { palette } = useTheme();

    const adls = useSelector(selectAdls);
    const dayToDays = useSelector(selectDayToDays);

    const { careRecipient, currentUser, allCaregivers } = useTeamMemberData();
    const isTeamAdmin = currentUser?.isTeamAdmin;
    const { trackPageView, trackEvent } = useAnalytics();

    const enabledAdls = useMemo(() => adls.filter((adl) => adl.enabled), [adls]);
    const enabledDayToDays = useMemo(
        () => dayToDays.filter((dayToDay) => dayToDay.enabled),
        [dayToDays]
    );

    const pronouns = careRecipient?.pronouns ? PronounLabel[careRecipient.pronouns] : undefined;

    useEffect(() => {
        trackPageView(PageName);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <InnerMaxWidthContainer>
            <Stack sx={{ gap: 4 }}>
                <Box>
                    <TopLevelPageHeader pageTitle="Care" />

                    {/* Care Recipient */}
                    {careRecipient && (
                        <Stack sx={{ gap: 2.5, alignItems: 'center' }}>
                            <TeamMemberAvatar size="large" teamMember={careRecipient} />
                            <Stack sx={{ alignItems: 'center' }}>
                                <Typography variant="h2" sx={{ fontWeight: 'bold' }}>
                                    {getTeamMemberDisplayName(careRecipient)}
                                </Typography>

                                {careRecipient.preferredName && (
                                    <Typography variant="body2">
                                        Preferred Name:{' '}
                                        <strong>{careRecipient.preferredName}</strong>
                                    </Typography>
                                )}

                                {pronouns && <Typography variant="body2">{pronouns}</Typography>}
                            </Stack>
                        </Stack>
                    )}
                </Box>

                {/* Care Needs Card */}
                {isTeamAdmin && !enabledAdls && (
                    <HeroCta
                        icon={faAddressCard}
                        title="Need to Know"
                        subtitle={`Let caregivers know the details that matter for caring for ${getTeamMemberDisplayName(
                            careRecipient,
                            {
                                firstOnly: true,
                            }
                        )}.`}
                        buttonText="Add Care Needs"
                        onClick={() => {
                            navigate({
                                to: '/care/adls/intro',
                            });
                        }}
                    />
                )}

                {/* Care Team Card */}
                {allCaregivers.length > 0 && isTeamAdmin && (
                    <Card
                        component={Link}
                        to="/care/team"
                        elevation={1}
                        sx={{ p: 2.5, cursor: 'pointer' }}
                    >
                        <Stack
                            direction="row"
                            sx={{ justifyContent: 'space-between', gap: 2.5, alignItems: 'center' }}
                        >
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                Care Team
                            </Typography>

                            <FontAwesomeIcon icon={faChevronRight} color={palette.purple?.[900]} />
                        </Stack>
                    </Card>
                )}

                {/* Care Needs Accordion */}
                <StyledAccordion defaultExpanded>
                    <StyledAccordionSummary
                        expandIcon={
                            <FontAwesomeIcon
                                icon={faChevronDown}
                                size="lg"
                                color={palette.primary.main}
                            />
                        }
                    >
                        <Typography
                            variant="body2"
                            sx={{ fontWeight: 'bold', textTransform: 'capitalize' }}
                        >
                            {getPronoun(careRecipient?.pronouns, 'possessive')} Care Needs
                        </Typography>
                    </StyledAccordionSummary>
                    {enabledAdls.length > 0 && (
                        <StyledAccordionDetails>
                            <Stack
                                sx={{
                                    gap: 2,
                                    borderTop: `1px solid ${palette.grey[300]}`,
                                    paddingTop: 2,
                                }}
                                divider={<Divider sx={{ borderColor: palette.grey[300] }} />}
                            >
                                {enabledAdls.map((adl, index) => (
                                    <Stack key={index} sx={{ gap: 1 }}>
                                        <Stack
                                            direction="row"
                                            sx={{ gap: 1, alignItems: 'center' }}
                                        >
                                            <FontAwesomeIcon
                                                icon={CareIcons[adl.name as string]}
                                                size="xl"
                                                color={palette.primary.main}
                                            />
                                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                                {AdlNames[adl.name as string]}
                                            </Typography>
                                        </Stack>
                                        {adl.notes && (
                                            <Typography variant="body2">{adl.notes}</Typography>
                                        )}
                                    </Stack>
                                ))}
                            </Stack>
                        </StyledAccordionDetails>
                    )}
                    {isTeamAdmin && (
                        <StyledAccordionActions>
                            <Stack sx={{ width: '100%' }}>
                                <Divider />
                                <Stack sx={{ gap: 2, pt: 2.5 }}>
                                    {enabledAdls.length === 0 && (
                                        <Typography variant="body2">
                                            {`Tell us about what ${getTeamMemberDisplayName(
                                                careRecipient,
                                                {
                                                    firstOnly: true,
                                                }
                                            )} needs help with`}
                                        </Typography>
                                    )}

                                    <Button
                                        fullWidth
                                        variant="contained"
                                        color="primaryInverse"
                                        onClick={() => {
                                            trackEvent('Add/UpdateADLs Button Clicked', {
                                                context: PageName,
                                            });
                                            navigate({
                                                to: '/care/adls/intro',
                                            });
                                        }}
                                        endIcon={
                                            enabledAdls.length > 0 && (
                                                <FontAwesomeIcon icon={faPencil} />
                                            )
                                        }
                                    >
                                        {enabledAdls.length > 0
                                            ? 'Update Care Needs'
                                            : 'Add Care Needs'}
                                    </Button>
                                </Stack>
                            </Stack>
                        </StyledAccordionActions>
                    )}
                </StyledAccordion>

                {/* Care Details Accordion */}
                <StyledAccordion defaultExpanded>
                    <StyledAccordionSummary
                        expandIcon={
                            <FontAwesomeIcon
                                icon={faChevronDown}
                                size="lg"
                                color={palette.primary.main}
                            />
                        }
                    >
                        <Typography
                            variant="body2"
                            sx={{ fontWeight: 'bold', textTransform: 'capitalize' }}
                        >
                            {getPronoun(careRecipient?.pronouns, 'possessive')} Care Details
                        </Typography>
                    </StyledAccordionSummary>
                    {enabledDayToDays.length > 0 && (
                        <StyledAccordionDetails>
                            <Stack
                                sx={{
                                    gap: 2,
                                    borderTop: `1px solid ${palette.grey[300]}`,
                                    paddingTop: 2,
                                }}
                                divider={<Divider sx={{ borderColor: palette.grey[300] }} />}
                            >
                                {enabledDayToDays.map((dayToDay, index) => (
                                    <Stack key={index} sx={{ gap: 1 }}>
                                        <Stack
                                            direction="row"
                                            sx={{ gap: 1, alignItems: 'center' }}
                                        >
                                            <FontAwesomeIcon
                                                icon={CareIcons[dayToDay.name as string]}
                                                size="xl"
                                                color={palette.primary.main}
                                            />
                                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                                {DayToDayNames[dayToDay.name as string]}
                                            </Typography>
                                        </Stack>
                                        {dayToDay.notes && (
                                            <Typography variant="body2">
                                                {dayToDay.notes}
                                            </Typography>
                                        )}
                                    </Stack>
                                ))}
                            </Stack>
                        </StyledAccordionDetails>
                    )}
                    {isTeamAdmin && (
                        <StyledAccordionActions>
                            <Stack sx={{ width: '100%' }}>
                                <Divider />
                                <Stack sx={{ gap: 2, pt: 2.5 }}>
                                    {enabledDayToDays.length === 0 && (
                                        <Typography variant="body2">
                                            {`Tell us about ${getTeamMemberDisplayName(
                                                careRecipient,
                                                {
                                                    firstOnly: true,
                                                }
                                            )} and what will help caregivers give ${getTeamMemberDisplayName(
                                                careRecipient,
                                                {
                                                    firstOnly: true,
                                                }
                                            )} the best care`}
                                        </Typography>
                                    )}

                                    <Button
                                        fullWidth
                                        variant="contained"
                                        color="primaryInverse"
                                        onClick={() => {
                                            trackEvent('Add/Update DayToDay Button Clicked', {
                                                context: PageName,
                                            });
                                            navigate({
                                                to: '/care/day-to-day/intro',
                                            });
                                        }}
                                        endIcon={
                                            enabledDayToDays.length > 0 && (
                                                <FontAwesomeIcon icon={faPencil} />
                                            )
                                        }
                                    >
                                        {enabledDayToDays.length > 0
                                            ? 'Update Details'
                                            : 'Add Details'}
                                    </Button>
                                </Stack>
                            </Stack>
                        </StyledAccordionActions>
                    )}
                </StyledAccordion>
            </Stack>

            {isMobile && <MainNav />}
        </InnerMaxWidthContainer>
    );
}
